<template>
  <div class="q-px-md row justify-between items-center">
    <q-toggle
      v-model="folderView"
      size="md"
      label="Folder View"
      class="text-sm text-weight-medium"
      left-label
    />
    <div>
      <PBtn v-if="!folderView" dense flat rounded>
        <FilterIcon v-if="selectedStatusFilter == 'all'" class="icon" />
        <FilterAppliedIcon v-else class="icon" />
        <PTooltip>Filter by status</PTooltip>

        <q-menu
          :offset="[30, 8]"
          anchor="bottom left"
          self="top middle"
          fit
          :style="{ borderRadius: '4px' }"
        >
          <q-list>
            <q-item
              v-for="({ label, value }, index) in filterItems"
              :key="index"
              v-close-popup
              clickable
              dense
              :class="selectedStatusFilter === value ? 'bg-primary-40' : ''"
              class="q-pa-sm"
              @click="() => onFilterClick(value)"
            >
              <q-item-section no-wrap>
                <q-item-label
                  :title="label"
                  class="text-neutral text-sm flex items-center"
                  lines="1"
                >
                  {{ label }}
                  <CheckmarkIcon
                    v-if="selectedStatusFilter === value"
                    class="icon--md text-primary q-ml-xl"
                  />
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </PBtn>
      <PBtn
        class="q-mx-xs"
        flat
        rounded
        dense
        @click="
          () => {
            openDocumentForm({
              open: true,
              level: 0,
              props: { isFolderView: unref(folderView) },
            });
            selectedStatusFilter = 'all';
          }
        "
      >
        <UploadIcon class="icon" />
      </PBtn>
      <PBtn dense flat rounded :loading="isDownloading">
        <MenuIcon class="icon" />
        <q-menu
          :offset="[30, 8]"
          anchor="bottom left"
          self="top middle"
          fit
          :style="{ borderRadius: '4px' }"
        >
          <q-list>
            <q-item
              v-for="(
                { label, onClick, disable, icon }, index
              ) in documentsGlobalActionItems"
              :key="index"
              v-close-popup
              clickable
              dense
              :disable="disable"
              @click="onClick"
            >
              <q-item-section no-wrap>
                <q-item-label
                  :title="label"
                  class="text-primary text-xs text-weight-medium flex items-center"
                  lines="1"
                >
                  <component :is="icon" class="icon--xs q-mr-xs"></component>
                  {{ label }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </PBtn>
    </div>
  </div>

  <PTable
    v-if="!isLoading"
    ref="pTable"
    v-model:selected="selected"
    :pagination="{
      rowsPerPage: 100,
    }"
    :columns="columns"
    :rows="computedUserDocuments"
    :row-key="folderView ? 'document_type' : '_id'"
    class="text-xs"
    table-header-class="text-neutral text-weight-medium"
    flat
    hide-bottom
    wrap-cells
    selection="multiple"
    :loading="isLoading"
  >
    <template
      v-for="(slot, index) of Object.keys($slots)"
      :key="index"
      #[slot]="slotProps"
    >
      <slot v-bind="slotProps" :name="slot"></slot>
    </template>

    <template #body-cell-name="props">
      <q-td :props="props">
        <div v-if="!folderView">
          <div
            v-if="props.value?.files?.length || props.value?.default_template"
          >
            <div class="flex items-center no-wrap q-mb-xs">
              <div class="text-xs text-neutral text-capitalize">
                {{ props.row.name }}
              </div>
              <span class="q-ml-xs">
                <ReviewIcon
                  v-if="props.value.status_cd == 'in_review'"
                  class="icon--sm text-neutral-60 block"
                />
                <RejectedIcon
                  v-else-if="props.value.status_cd == 'rejected'"
                  class="icon--sm text-error-60 block"
                />
                <RejectedIcon
                  v-else-if="props.value.status_cd == 'expired'"
                  class="icon--sm text-error-60 block"
                />
                <ApprovedIcon
                  v-else-if="props.value.status_cd == 'approved'"
                  class="icon--sm text-success-70 block"
                />
                <RejectedIcon
                  v-else-if="
                    props.value.status_cd == 'pending' &&
                    props.value.default_template
                  "
                  class="icon--sm text-warning-60 block"
                />
                <ReviewIcon
                  v-else-if="props.value.status_cd == 'awaiting_response'"
                  class="icon--sm text-link block"
                />
                <SignIcon
                  v-else-if="props.value.status_cd == 'partially_signed'"
                  class="icon--sm text-warning-50 block"
                />
                <SignCompleteIcon
                  v-else-if="props.value.status_cd == 'completed'"
                  class="icon--sm text-success-70 block"
                />
                <PTooltip
                  >{{ getTooltipStatusValue(props.value.status_cd) }}
                  {{
                    props.value.status_cd == "rejected"
                      ? ". " + (props.value.reason ?? "")
                      : ""
                  }}</PTooltip
                >
              </span>
            </div>

            <div v-if="props.value.files?.length">
              <div
                v-for="(file, index) in props.value.files"
                :key="index"
                class="flex items-center no-wrap q-mb-xxs"
              >
                <span
                  class="text-xs text-weight-medium text-primary-100 cursor-pointer"
                  @click="viewFile(file)"
                >
                  {{ file.name }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-else
            class="flex items-center text-xs text-weight-medium text-primary-100 text-capitalize"
          >
            <span>{{ props.value.name }}</span>
            <span
              v-if="props.value.status_cd == 'awaiting_response'"
              class="q-ml-xs"
            >
              <ReviewIcon class="icon--sm text-link block" />
              <PTooltip>Awaiting Response</PTooltip>
            </span>
            <span
              v-else-if="
                props.value.status_cd == 'pending' &&
                props.value.default_template
              "
              class="q-ml-xs"
            >
              <RejectedIcon class="icon--sm text-warning-60 block" />
              <PTooltip>Pending</PTooltip>
            </span>
          </div>
          <div class="flex items-center">
            <PBadge
              :label="props.value.sub_category"
              :type="props.value.sub_category"
              class="text-capitalize q-my-xs"
            />
            <PBadge
              v-if="
                props.value.default_template &&
                props.value.label &&
                !props.value.signatories?.length
              "
              :label="'Signed'"
              :type="'signed'"
              class="text-capitalize q-my-xs q-ml-xs"
            />
            <PBadge
              v-else-if="
                props.value.default_template &&
                !props.value.label &&
                !props.value.signatories?.length
              "
              :label="'Unsigned'"
              :type="'unsigned'"
              class="text-capitalize q-my-xs q-ml-xs"
            />
            <Menu
              v-else-if="
                props.value.default_template && props.value.signatories?.length
              "
              placement="auto-start"
            >
              <PBadge
                :label="props.value.label ? 'Signed' : 'Unsigned'"
                :type="props.value.label ? 'signed' : 'unsigned'"
                class="text-capitalize q-my-xs q-ml-xs"
              />
              <template #popper>
                <template
                  v-for="({ name, email, signed, signed_date }, index) in props
                    .value.signatories"
                  :key="index"
                >
                  <div class="q-mb-md">
                    <div>
                      <span class="text-neutral text-xs">
                        <span class="text-capitalize">{{ name }}</span
                        ><span>{{ " | " + email + " | " }}</span>
                      </span>
                      <span
                        class="text-xs text-weight-medium"
                        :class="signed ? 'text-neutral-80' : 'text-error-60'"
                        >{{ signed ? "Signed" : "Unsigned" }}
                      </span>
                    </div>
                    <div class="text-neutral-70 text-xxs q-mt-xxs">
                      {{
                        formatDate(signed_date, "DD MMM YYYY, h:mm a") || "-"
                      }}
                    </div>
                  </div>
                </template>
              </template>
            </Menu>
          </div>

          <div
            v-if="props.value.files?.length || props.value.default_template"
            class="text-xxs text-neutral-70"
          >
            {{
              formatDate(
                props.value.updated_date || props.value.created_date,
                "DD MMM YYYY, h:mm a",
              )
            }}
            .
            {{ props.value.updated_by?.name || props.value.created_by?.name }}
          </div>
        </div>
        <div v-else class="flex items-center text-xs text-neutral no-wrap">
          <span><FolderIcon class="icon--xs q-mr-xs" /></span>
          <span>
            {{
              useChangeCase(props?.value?.document_type || "", "capitalCase")
                .value
            }}
            ({{ props.value.documents?.length }})
          </span>
        </div>
      </q-td>
    </template>

    <template #body-cell-status="props">
      <q-td :props="props">
        <div class="text-xs text-neutral row justify-between">
          <div
            v-for="(
              { documentStatus, icon, iconClass, contentClass, label }, index
            ) in statuses"
            :key="index"
            class="flex items-center"
          >
            <Menu class="flex items-center">
              <component :is="icon" :class="iconClass"></component>
              <span>{{ props.value[documentStatus].length }}</span>
              <template #popper>
                <div v-if="props.value[documentStatus].length">
                  <div
                    v-for="(document, idx) in props.value[documentStatus]"
                    :key="idx"
                    :class="
                      contentClass ??
                      'text-xs q-my-xs text-primary-100 text-weight-medium'
                    "
                  >
                    <span v-if="!document.files?.length">{{
                      document.name
                    }}</span>
                    <div
                      v-for="(file, indx) in document.files"
                      v-else
                      :key="indx"
                      class="q-my-xs"
                    >
                      <span class="cursor-pointer" @click="viewFile(file)">{{
                        file.name
                      }}</span>
                    </div>
                  </div>
                </div>
                <div v-else>No documents are in {{ label }} state</div>
              </template>
            </Menu>
          </div>
        </div>
      </q-td>
    </template>

    <template #body-cell-required="props">
      <q-td :props="props">
        <PCheckbox
          v-model="props.row.required"
          :disable="!props.row.is_allowed_to_change"
          @update:model-value="handleDocumentRequirementChange(props.row)"
        />
      </q-td>
    </template>

    <template #body-cell-timelines="props">
      <q-td :props="props">
        <PBtn
          class="text-primary-100 text-xs text-weight-medium"
          dense
          flat
          text
          :loading="isTimelineLoading[props.row._id]"
          @click="openTimelines(props.row)"
        >
          {{ props.value.length }}
        </PBtn>
      </q-td>
    </template>

    <template v-if="!selected.length" #body-cell-action="props">
      <q-td :props="props">
        <div class="flex no-wrap">
          <PBtn
            v-if="!folderView"
            dense
            flat
            rounded
            :disable="
              !props.row.files?.length ||
              props.row.status_cd == 'approved' ||
              (props.row.default_template && !props.row.label)
            "
            @click="
              handleDocumentStatusChange({ row: props.row, status: 'approved' })
            "
          >
            <CheckmarkIcon class="icon--md text-success-70" />
            <PTooltip> Approve </PTooltip>
          </PBtn>
          <PBtn
            v-if="!folderView"
            dense
            flat
            rounded
            :disable="
              !props.row.files?.length ||
              props.row.status_cd == 'approved' ||
              props.row.status_cd == 'rejected' ||
              (props.row.default_template && !props.row.label)
            "
            @click="
              handleDocumentStatusChange({ row: props.row, status: 'rejected' })
            "
          >
            <CloseIcon class="icon--md text-error-70" />
            <PTooltip> Reject </PTooltip>
          </PBtn>
          <PBtn
            dense
            flat
            rounded
            :disable="
              (!folderView &&
                !props.row.default_template &&
                !props.row.files?.length &&
                !props.row.default_template_url?.length) ||
              (folderView && !props.row.documents?.length)
            "
          >
            <MenuIcon class="icon--md" />
            <q-menu
              :offset="[30, 8]"
              anchor="bottom left"
              self="top middle"
              fit
              :style="{ borderRadius: '4px' }"
            >
              <q-list>
                <q-item
                  v-for="(
                    { label, onClick, disable, icon }, index
                  ) in documentActionItems(props.row)"
                  :key="index"
                  v-close-popup
                  clickable
                  dense
                  :disable="disable"
                  @click="() => onClick(props.row)"
                >
                  <q-item-section no-wrap>
                    <q-item-label
                      :title="label"
                      class="text-primary text-xs text-weight-medium flex items-center"
                      lines="1"
                    >
                      <component
                        :is="icon"
                        class="icon--xs q-mr-xs"
                      ></component>
                      {{ label }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </PBtn>
        </div>
      </q-td>
    </template>
  </PTable>

  <q-linear-progress v-else-if="isLoading" indeterminate />
</template>
<script setup>
import ReviewIcon from "assets/icons/time-circle.svg";
import SignIcon from "assets/icons/sign.svg";
import SignCompleteIcon from "assets/icons/sign_complete.svg";
import RejectedIcon from "assets/icons/exclamation-round.svg";
import ApprovedIcon from "assets/icons/check-round-outline.svg";
import CheckmarkIcon from "assets/icons/checkmark.svg";
import CloseIcon from "assets/icons/close.svg";
import MenuIcon from "assets/icons/menu.svg";
import EditIcon from "assets/icons/edit.svg";
import DownloadIcon from "assets/icons/download.svg";
import FilterIcon from "assets/icons/filter.svg";
import FilterAppliedIcon from "assets/icons/filter-applied.svg";
import RemoveIcon from "assets/icons/remove.svg";
import SendSignatureIcon from "assets/icons/start_process_4.svg";
import UploadIcon from "assets/icons/re-upload.svg";
import FolderIcon from "assets/icons/folder.svg";
import VerifiedDocument from "assets/icons/verified_document.svg";
import RejectedDocument from "assets/icons/reject_document.svg";
import SendForSignatureDialogForm from "./SendForSignatureDialogForm.vue";
import { Menu } from "floating-vue";
import "floating-vue/dist/style.css";

const { userDocuments, userDocumentsStatuses } = storeToRefs(useUserStore());
const {
  getUserDocuments,
  putUserDocumentRequirement,
  putUserDocumentStatus,
  getUserDocumentsZip,
  toggleTimelineDrawer,
  toggleDocumentForm,
} = useUserStore();
import { date, useQuasar } from "quasar";
import PDialogForm from "src/components/PDialogForm.vue";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
const { getAuditLogs } = useAuditLogsStore();

const $q = useQuasar();

const computedUserDocuments = computed(() => unref(userDocuments));
const computedUserDocumentsStatuses = computed(() =>
  unref(userDocumentsStatuses),
);

const { downloadFile, viewFile } = useFile();

const { formatDate } = date;

const folderView = ref(false);

const pTable = ref(null);

const isLoading = ref(false);

const isTimelineLoading = reactive({});

const getTooltipStatusValue = (status) => {
  switch (status) {
    case "awaiting_response":
      return "Awaiting Response";

    case "pending":
      return "Pending";

    case "approved":
      return "Approved";

    case "in_review":
      return "In Review";

    case "partially_signed":
      return "Partially Signed";

    case "completed":
      return "Completed";

    case "rejected":
      return "Rejected";
    case "expired":
      return "Expired";
  }
};

const openDocumentForm = (state = {}) => {
  toggleDocumentForm(state);
};

const openTimelines = async (row) => {
  if (!row.timelines?.length) return;

  try {
    isTimelineLoading[row._id] = true;

    const response = await getAuditLogs({
      params: { entity_id: row._id, entity: "UserDocuments" },
    });

    isTimelineLoading[row._id] = false;

    toggleTimelineDrawer({
      open: true,
      level: 0,
      props: {
        document: row.name,
        timelines: response?.data?.data?.data || [],
        entity: "Document",
      },
    });
  } catch (err) {
    console.log(err);

    isTimelineLoading[row._id] = false;
  }
};

const handleSendForSignatureDialog = (fileName, documentId) => {
  $q.dialog({
    component: SendForSignatureDialogForm,
    componentProps: {
      fileName: fileName,
      documentId: documentId,
    },
  });
};

const isDownloading = ref(false);

const documentsGlobalActionItems = computed(() => {
  if (unref(folderView)) {
    return [
      {
        label: "Download as zip",
        onClick: () => handleBulkDownload({ isFolderView: unref(folderView) }),
        disable: !unref(selected).length || unref(noFilesAttached),
        icon: FolderIcon,
      },
    ];
  }

  return [
    {
      label: "Download as zip",
      onClick: () => handleBulkDownload({ isFolderView: unref(folderView) }),
      disable: !unref(selected).length || unref(noFilesAttached),
      icon: FolderIcon,
    },
    {
      label: "Bulk Approve",
      onClick: () => handleBulkStatusChange("approved"),
      disable: !unref(selected).length,
      icon: VerifiedDocument,
    },
    {
      label: "Bulk Reject",
      onClick: () => handleBulkStatusChange("rejected"),
      disable: !unref(selected).length,
      icon: RejectedDocument,
    },
  ];
});

const selectedStatusFilter = ref("all");

const onFilterClick = (status) => {
  selectedStatusFilter.value = status;
};

const filterItems = computed({
  get() {
    let documentStatuses = unref(computedUserDocumentsStatuses);
    let items = [
      {
        label: `All (${documentStatuses["all"] || 0})`,
        value: "all",
      },
      {
        label: `Pending (${documentStatuses["pending"] || 0})`,
        value: "pending",
      },
      {
        label: `In Review (${documentStatuses["in_review"] || 0})`,
        value: "in_review",
      },
      {
        label: `Approved (${documentStatuses["approved"] || 0})`,
        value: "approved",
      },
      {
        label: `Rejected (${documentStatuses["rejected"] || 0})`,
        value: "rejected",
      },
      {
        label: `Expired (${documentStatuses["expired"] || 0})`,
        value: "expired",
      },
    ];

    for (const status in documentStatuses) {
      if (!items.some((item) => item.value === status)) {
        items.push({
          label: `${useChangeCase(status.replace("_", " "), "capitalCase").value} (${documentStatuses[status]})`,
          value: status,
        });
      }
    }

    return items;
  },
});

const documentActionItems = (document) => {
  if (unref(folderView)) {
    return [
      {
        label: "Download as zip",
        onClick: (row) => handleBulkDownload({ row, isFolderView: true }),
        disable:
          document.in_review_documents.length == 0 &&
          document.approved_documents.length == 0 &&
          document.rejected_documents.length == 0,
        icon: FolderIcon,
      },
    ];
  }

  let is_special_document = document.default_template;
  let items = [
    {
      label: "Download File",
      onClick: async (row) => {
        isDownloading.value = true;

        if (row.files?.length == 1) {
          downloadFile(row.files[0]);
        } else {
          await getUserDocumentsZip({
            user_id: row.user_id,
            user_document_id: [row._id],
          });
        }

        isDownloading.value = false;
      },
      disable: is_special_document && !document.files?.length,
      icon: DownloadIcon,
    },
    {
      label: "Edit",
      onClick: (row) =>
        openDocumentForm({
          edit: true,
          level: 0,
          open: true,
          props: { document: row, isFolderView: unref(folderView) },
        }),
      disable: false,
      icon: EditIcon,
    },
    {
      label: "Remove",
      onClick: null,
      disable: true,
      icon: RemoveIcon,
    },
  ];

  if (document?.default_template) {
    items.push({
      label: "Send for signature",
      onClick: () => handleSendForSignatureDialog(document.name, document._id),
      disable:
        document.contract_template_id == null ||
        !(document.status_cd == "pending" || document.status_cd == "rejected"),
      icon: SendSignatureIcon,
    });
  }

  return items;
};

const columns = computed(() => {
  if (unref(folderView)) {
    return [
      {
        align: "left",
        name: "name",
        label: "Name",
        field: (row) => row,
      },
      {
        align: "center",
        name: "status",
        label: "Status",
        field: (row) => row,
      },
      {
        align: "left",
        name: "updated_date",
        label: "Last Updated",
        field: (row) => row.last_updated_date,
        format: (value) => formatDate(value, "DD MMM YYYY, h:mm a") || "-",
      },
      {
        align: "left",
        classes: "sticky--right",
        headerClasses: "sticky--right",
        name: "action",
        label: "",
      },
    ];
  }

  return [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: (row) => row,
    },
    {
      align: "center",
      name: "required",
      label: "Mandatory?",
      field: "required",
    },
    {
      align: "center",
      name: "timelines",
      label: "Timelines",
      field: (row) => row.timelines,
    },
    {
      align: "left",
      classes: "sticky--right",
      headerClasses: "sticky--right bg-white",
      name: "action",
      label: "",
    },
  ];
});

const statuses = computed({
  get() {
    return [
      {
        documentStatus: "pending_documents",
        label: "Pending",
        icon: RejectedIcon,
        iconClass: "icon--sm text-warning-60 q-mr-xxs",
        contentClass: "text-xs q-my-xs",
      },
      {
        documentStatus: "in_review_documents",
        label: "In Review",
        icon: ReviewIcon,
        iconClass: "icon--sm text-neutral-60 q-mr-xxs",
      },
      {
        documentStatus: "approved_documents",
        label: "Approved",
        icon: ApprovedIcon,
        iconClass: "icon--sm text-success-70 q-mr-xxs",
      },
      {
        documentStatus: "rejected_documents",
        label: "Rejected",
        icon: RejectedIcon,
        iconClass: "icon--sm text-error-60 q-mr-xxs",
      },
    ];
  },
});

const selected = ref([]);

const prepareCategories = (documents) =>
  documents
    .filter((doc) => doc.documents?.length != doc.pending_documents.length)
    .map((doc) => doc.document_type);

const prepareDocumentIds = (documents) =>
  documents.filter((doc) => doc.files?.length).map((doc) => doc._id);

const handleBulkDownload = async ({ row, isFolderView = false }) => {
  let selectedDocuments = unref(selected);

  if (isFolderView) {
    if (row?.documents?.length != row?.pending_documents?.length) {
      isDownloading.value = true;

      await getUserDocumentsZip({
        user_id: row.documents[0]?.user_id,
        sub_category: row.document_type,
      });

      isDownloading.value = false;
    } else if (selectedDocuments.length > 0) {
      const selectedCategories = prepareCategories(selectedDocuments);

      if (selectedCategories.length) {
        isDownloading.value = true;

        await getUserDocumentsZip({
          user_id: selectedDocuments[0].documents[0].user_id,
          sub_category: selectedCategories,
        });

        isDownloading.value = false;
      }
    }
  } else if (selectedDocuments.length > 0) {
    const selectedIds = prepareDocumentIds(selectedDocuments);

    if (selectedIds.length) {
      isDownloading.value = true;

      await getUserDocumentsZip({
        user_id: selectedDocuments[0].user_id,
        user_document_id: selectedIds,
      });

      isDownloading.value = false;
    }
  }
};

const handleBulkStatusChange = async (status) => {
  const selectedDocuments = unref(selected);

  if (selectedDocuments.length <= 0) {
    return;
  }

  let selectedDocumentsIds = [];
  let user_id = selectedDocuments[0].user_id;

  for (const document of selectedDocuments) {
    if (document.files?.length) {
      selectedDocumentsIds.push(document._id);
    }
  }

  if (selectedDocumentsIds.length) {
    let rows = { user_id, selectedDocumentsIds };

    handleDocumentStatusChange({
      row: rows,
      status: status,
      isBulkChange: true,
    });
  }
};

const handleDocumentRequirementChange = async (row) => {
  await putUserDocumentRequirement({
    user_id: row.user_id,
    user_document_id: row._id,
    required: row.required,
  });
};

const handleDocumentStatusChange = async ({
  row,
  status,
  isBulkChange = false,
}) => {
  if (status == "rejected") {
    const rejectDialog = $q.dialog({
      component: PDialogForm,
      componentProps: {
        title: `Reject Document`,
        submitLabel: "Submit",
        form: {
          input: {
            placeholder: "Enter reason",
            label: "Rejection Reason",
            labelClasses: "text-sm text-neutral",
            help: "Please state the reason to reject this document",
            helpClasses: "text-xs text-neutral-70 q-mt-none",
            validations: {
              label: "Rejection reason",
              required: true,
            },
          },
        },
        cancellable: false,
        onConfirm: async (rejectReason) => {
          await putUserDocumentStatus({
            document_id: isBulkChange ? row.selectedDocumentsIds : [row._id],
            seller_id: row.user_id,
            status_cd: status,
            reason: rejectReason.value,
          });

          rejectDialog.hide();
        },
      },
    });
  } else {
    await putUserDocumentStatus({
      document_id: isBulkChange ? row.selectedDocumentsIds : [row._id],
      seller_id: row.user_id,
      status_cd: status,
      reason: "",
    });
  }
};

watch(folderView, async (folderViewValue) => {
  isLoading.value = true;

  if (folderViewValue) {
    await getUserDocuments({ groupBySubCategories: 1 });
  } else {
    selectedStatusFilter.value = "all";

    await getUserDocuments();
  }

  selected.value = [];

  isLoading.value = false;
});

watch(selectedStatusFilter, async (selectedStatusFilterValue) => {
  isLoading.value = true;

  await getUserDocuments({
    status_cd:
      selectedStatusFilterValue == "all" ? null : selectedStatusFilterValue,
  });

  isLoading.value = false;
});

const noFilesAttached = ref(true);

watch(computedUserDocuments, (userDocs) => {
  if (unref(folderView)) {
    for (const doc of userDocs) {
      if (
        doc.in_review_documents.length != 0 ||
        doc.approved_documents.length != 0 ||
        doc.rejected_documents.length != 0
      ) {
        noFilesAttached.value = false;
      }
    }
  } else {
    for (const doc of userDocs) {
      if (doc.files?.length) {
        noFilesAttached.value = false;
      }
    }
  }
});

onBeforeMount(async () => {
  isLoading.value = true;

  await getUserDocuments();

  isLoading.value = false;
});
</script>
